/**
 * Central SEO configuration for ChesiSite_Web.
 * Targets: WMS (warehouse management), AIDC (barcode scanners, mobile computers, RFID).
 */

export const SITE_NAME = 'Chesi Mobile Solutions';
export const SITE_NAME_SHORT = 'Chesi';

export const SITE_URL =
  process.env.REACT_APP_SITE_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://www.chesimobile.com'
    : 'http://localhost:3000');

export const DEFAULT_OG_IMAGE = `${SITE_URL}/chesi-logo-icon.png`;

export const ORGANIZATION = {
  name: SITE_NAME,
  legalName: 'Chesi Mobile Solutions',
  url: SITE_URL,
  logo: `${SITE_URL}/chesi-logo-icon.png`,
  email: 'info@chesimobile.com',
  phone: '+254711435629',
  address: {
    addressCountry: 'KE',
    addressLocality: 'Nairobi',
    addressRegion: 'Nairobi'
  },
  sameAs: [
    'https://www.linkedin.com/company/chesi-mobile-solutions'
  ]
};

/** Primary keyword clusters for ranking */
export const KEYWORD_CLUSTERS = {
  wms: [
    'warehouse management system',
    'WMS software',
    'WMS Africa',
    'WMS Kenya',
    'cloud warehouse management',
    'inventory management system',
    'warehouse automation',
    'stock control software',
    'distribution management',
    'fulfillment software'
  ],
  aidc: [
    'AIDC products',
    'automatic identification and data capture',
    'barcode scanners',
    'mobile computers',
    'handheld scanners',
    'barcode printers',
    'Honeywell scanners',
    'Honeywell mobile computers',
    'RFID readers',
    'tablet computers warehouse',
    'receipt printers',
    'barcode labels',
    'enterprise mobility',
    'rugged mobile devices'
  ],
  local: [
    'Kenya',
    'East Africa',
    'Africa',
    'Nairobi',
    'barcode solutions Kenya',
    'warehouse software Kenya'
  ]
};

export const DEFAULT_KEYWORDS = [
  ...KEYWORD_CLUSTERS.wms.slice(0, 6),
  ...KEYWORD_CLUSTERS.aidc.slice(0, 8),
  ...KEYWORD_CLUSTERS.local
].join(', ');

export const PAGE_SEO = {
  home: {
    title: 'ChesiWMS & AIDC Products | Warehouse Management System & Barcode Solutions in Africa',
    description:
      'Chesi delivers cloud Warehouse Management System (WMS) software and AIDC hardware — Honeywell barcode scanners, mobile computers, RFID readers & printers — for warehouses and retailers in Kenya and across Africa. Inventory control, picking, receiving & enterprise mobility.',
    keywords: DEFAULT_KEYWORDS,
    path: '/'
  },
  shop: {
    title: 'Shop AIDC Products | Barcode Scanners, Mobile Computers & Printers | Chesi',
    description:
      'Buy AIDC products online: Honeywell barcode scanners, rugged mobile computers, barcode printers, RFID readers and labels. Enterprise-grade automatic identification and data capture equipment with expert support in Kenya & East Africa.',
    keywords: [...KEYWORD_CLUSTERS.aidc, ...KEYWORD_CLUSTERS.local].join(', '),
    path: '/shop'
  },
  shopBrands: {
    title: 'Shop by Brand | Honeywell & AIDC Brands | Chesi',
    description:
      'Browse AIDC products by brand. Authorized Honeywell barcode scanners, mobile computers and warehouse hardware for African businesses.',
    keywords: 'Honeywell, AIDC brands, barcode scanner brands, mobile computer brands, Kenya',
    path: '/shop/brands'
  },
  search: {
    title: 'Search AIDC Products | Chesi Shop',
    description: 'Search barcode scanners, mobile computers, printers and AIDC accessories by name, SKU or brand.',
    path: '/shop/search'
  },
  about: {
    title: 'About Chesi | WMS & AIDC Solutions for Africa',
    description:
      'Chesi Mobile Solutions provides warehouse management software and AIDC hardware to help African businesses optimize inventory, logistics and retail operations.',
    path: '/about'
  },
  contact: {
    title: 'Contact Chesi | WMS & AIDC Sales Support',
    description: 'Contact Chesi for warehouse management system demos, AIDC product quotes and technical support in Kenya and East Africa.',
    path: '/contact'
  },
  faq: {
    title: 'FAQ | Chesi WMS & AIDC Products',
    description: 'Frequently asked questions about ChesiWMS warehouse management software and AIDC barcode scanning solutions.',
    path: '/faq'
  },
  shippingPolicy: {
    title: 'Shipping Policy | Chesi AIDC Products Kenya',
    description: 'Shipping and delivery information for AIDC hardware orders from Chesi Mobile Solutions in Kenya and East Africa.',
    path: '/shipping-policy'
  },
  returnsPolicy: {
    title: 'Returns Policy | Chesi AIDC Products',
    description: 'Returns and warranty policy for barcode scanners, mobile computers and AIDC products purchased from Chesi.',
    path: '/returns-policy'
  },
  privacyPolicy: {
    title: 'Privacy Policy | Chesi Mobile Solutions',
    description: 'How Chesi Mobile Solutions collects, uses and protects your personal data when you use our WMS and ecommerce services.',
    path: '/privacy-policy'
  },
  terms: {
    title: 'Terms of Service | Chesi Mobile Solutions',
    description: 'Terms and conditions for using ChesiWMS warehouse management software and purchasing AIDC products from Chesi.',
    path: '/terms'
  },
  raiseTicket: {
    title: 'Support Ticket | ChesiWMS & AIDC Support',
    description: 'Raise a support ticket for ChesiWMS warehouse management software or AIDC hardware. Expert technical support in Kenya.',
    path: '/raise-ticket'
  }
};

export function absoluteUrl(path = '/') {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}

export function truncate(text, max = 160) {
  if (!text) return '';
  const clean = String(text).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 3).trim()}...`;
}

export function categorySeo(categorySlug, categoryName) {
  const name = categoryName || categorySlug?.replace(/-/g, ' ') || 'Products';
  return {
    title: `${name} | AIDC Products | Chesi Kenya`,
    description: `Shop ${name.toLowerCase()} — barcode scanners, mobile computers, printers and AIDC equipment. Enterprise-grade ${name.toLowerCase()} with fast delivery and expert support in Kenya & Africa.`,
    keywords: `${name}, AIDC, ${name} Kenya, barcode, warehouse, ${KEYWORD_CLUSTERS.aidc.slice(0, 4).join(', ')}`,
    path: `/shop/category/${categorySlug}`
  };
}

export function brandSeo(brandName, brandSlug) {
  return {
    title: `${brandName} AIDC Products | Barcode & Mobile Solutions | Chesi`,
    description: `Shop ${brandName} barcode scanners, mobile computers and AIDC products. Authorized ${brandName} equipment for warehouses and retail in Kenya and East Africa.`,
    keywords: `${brandName}, ${brandName} scanner, ${brandName} mobile computer, AIDC, Kenya`,
    path: `/shop/brand/${brandSlug}`
  };
}

export function productSeo(product) {
  const name = product?.name || 'Product';
  const sku = product?.sku ? ` (${product.sku})` : '';
  const brand = product?.brand_name ? ` by ${product.brand_name}` : '';
  const slug = product?.slug || product?.id;
  return {
    title: `${name}${sku} | AIDC Product${brand} | Chesi`,
    description: truncate(
      product?.short_description ||
        `Buy ${name}${brand}. Enterprise AIDC product — barcode scanner, mobile computer or warehouse hardware. SKU ${product?.sku || 'N/A'}. Available from Chesi in Kenya & Africa.`,
      160
    ),
    keywords: [name, product?.sku, product?.brand_name, 'AIDC', 'barcode', 'Kenya'].filter(Boolean).join(', '),
    path: `/shop/product/${slug}`,
    image: product?.primary_image_url || product?.images?.[0]?.image_url
  };
}

/** Routes that should not be indexed */
export const NOINDEX_PATHS = [
  '/cart',
  '/checkout',
  '/login',
  '/register',
  '/account',
  '/wishlist',
  '/forgot-password',
  '/reset-password',
  '/order-success'
];

export function shouldNoIndex(pathname) {
  const path = pathname?.split('?')[0] || '';
  return NOINDEX_PATHS.some(
    (p) => path === p || path.startsWith(`${p}/`)
  );
}

const CMS_PAGE_SEO = {
  about: PAGE_SEO.about,
  contact: PAGE_SEO.contact,
  faq: PAGE_SEO.faq,
  'shipping-policy': PAGE_SEO.shippingPolicy,
  'returns-policy': PAGE_SEO.returnsPolicy,
  'privacy-policy': PAGE_SEO.privacyPolicy,
  terms: PAGE_SEO.terms
};

export function cmsPageSeo(slug, page) {
  const preset = CMS_PAGE_SEO[slug];
  if (preset) return preset;
  const title = page?.title || slug?.replace(/-/g, ' ') || 'Page';
  return {
    title: `${title} | Chesi WMS & AIDC`,
    description: truncate(page?.meta_description || `${title} — Chesi Mobile Solutions warehouse management and AIDC products.`),
    path: slug ? `/${slug}` : '/'
  };
}
